import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Check, X as XIcon, Phone, MapPin, Lightning } from '@phosphor-icons/react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGE_LABELS = {
    economy: 'Economy',
    business: 'Business',
    first_class: 'First Class',
    spoedopleiding_5: 'Spoed 5u',
    spoedopleiding_10: 'Spoed 10u',
    spoedopleiding_16_5: 'Spoed 16,5u',
    spoedopleiding_25: 'Spoed 25u',
};

/**
 * Instructor's "Nieuwe aanmeldingen" card on the Home tab. Shows every
 * self-registered student who's still waiting for approval, with big
 * Accepteer / Weigeren buttons per row.
 */
export default function PendingSignupsCard({ onAfterAction }) {
    const [pending, setPending] = useState([]);
    const [busy, setBusy] = useState(null);

    const load = async () => {
        try {
            const { data } = await axios.get(`${API}/students/pending`, { withCredentials: true });
            setPending(data || []);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        load();
        // Refresh whenever the notification bell fires a signup event
        const t = setInterval(load, 30000);   // gentle polling
        return () => clearInterval(t);
    }, []);

    const decide = async (student, action) => {
        if (busy) return;
        setBusy(student.id);
        try {
            await axios.post(
                `${API}/students/${student.id}/approve`,
                { action },
                { withCredentials: true }
            );
            setPending(prev => prev.filter(s => s.id !== student.id));
            toast.success(action === 'accept' ? `${student.name} geaccepteerd ✅` : 'Aanmelding afgewezen');
            if (onAfterAction) onAfterAction(action, student);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Actie mislukt');
        } finally {
            setBusy(null);
        }
    };

    if (pending.length === 0) return null;

    return (
        <div
            className="mt-4 bg-gradient-to-br from-[#D4A537]/15 via-[#D4A537]/5 to-transparent border-2 border-[#D4A537]/40 rounded-xl p-4"
            data-testid="pending-signups-card"
        >
            <div className="flex items-center gap-2 mb-3">
                <div className="relative">
                    <UserPlus size={22} weight="fill" className="text-[#D4A537]" />
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#1A202C]" data-testid="pending-signups-badge">
                        {pending.length}
                    </span>
                </div>
                <div>
                    <div className="text-sm font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Nieuwe {pending.length === 1 ? 'aanmelding' : 'aanmeldingen'}
                    </div>
                    <div className="text-[11px] text-gray-400">
                        Bekijk en accepteer of weiger elke nieuwe leerling
                    </div>
                </div>
            </div>

            <div className="space-y-2">
                {pending.map(s => (
                    <div
                        key={s.id}
                        className="bg-[#2D3748] border border-[#4A5568] rounded-lg p-3"
                        data-testid={`pending-signup-${s.id}`}
                    >
                        <div className="flex items-start gap-2 mb-2">
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-white truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                    {s.name || 'Naamloos'}
                                </div>
                                <div className="text-[11px] text-gray-400 truncate">{s.email}</div>
                            </div>
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#D4A537]/15 text-[#D4A537] border border-[#D4A537]/40 flex-shrink-0">
                                {PACKAGE_LABELS[s.package_type] || s.package_type}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-300 mb-2.5">
                            {s.phone && (
                                <span className="flex items-center gap-1"><Phone size={10} />{s.phone}</span>
                            )}
                            {s.address && (
                                <span className="flex items-center gap-1"><MapPin size={10} />{s.address}</span>
                            )}
                            {s.extra_lessons > 0 && (
                                <span className="flex items-center gap-1 text-[#D4A537]"><Lightning size={10} weight="fill" />+{s.extra_lessons} extra</span>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <Button
                                onClick={() => decide(s, 'reject')}
                                disabled={busy === s.id}
                                variant="outline"
                                size="sm"
                                className="border-red-500/60 bg-red-500/10 text-red-300 hover:bg-red-500/20 font-semibold"
                                data-testid={`pending-reject-${s.id}`}
                            >
                                <XIcon size={14} weight="bold" className="mr-1" /> Weiger
                            </Button>
                            <Button
                                onClick={() => decide(s, 'accept')}
                                disabled={busy === s.id}
                                size="sm"
                                className="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold"
                                data-testid={`pending-accept-${s.id}`}
                            >
                                <Check size={14} weight="bold" className="mr-1" />
                                {busy === s.id ? 'Bezig…' : 'Accepteer'}
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
